import React, { useCallback, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { getBackgroundStyle, getBorderStyle } from 'zoomforth_codesbane_viewer/lib/section_backgrounds.js';
import { getActiveTabId } from 'zoomforth_codesbane_viewer/reducers/index.js';
import { setActiveTabId } from 'zoomforth_codesbane_viewer/action_creators/viewer.js';
import SectionsRoot from '../sections_root.jsx';
import 'zoomforth_codesbane_editor/in_page/section_editors/tabs/tabs_section.less';

const TabsSection = ({ section, innerBackgroundData, borderData, activeTabId, setActiveTabId, ...props }) => {
  const tabs = section.tabs || [];
  
  const currentActiveTabId = activeTabId || (tabs.length > 0 ? tabs[0].id : null);

  const handleTabClick = useCallback((tabId, event) => {
    setActiveTabId(section.id, tabId);
    
    // Mobile behaviour - scroll the clicked button into view to allow user see buttons that are offscreen
    if (event && event.currentTarget) {
      event.currentTarget.scrollIntoView({
        behavior: 'smooth',
        block: 'nearest',
        inline: 'center'
      });
    }
  }, [section.id, setActiveTabId]);

  // Refs for tabs navigation elements to attach non-passive wheel listeners
  const tabsNavAboveRef = useRef(null);
  const tabsNavInsideRef = useRef(null);

  const layoutSettings = section.layoutSettings || {};
  const { tabPlacement = 'inside' } = layoutSettings;

  useEffect(() => {
    const handleWheel = (event) => {
      if (event.deltaX) { return }
      const container = event.currentTarget;
      if (container.scrollWidth > container.clientWidth) {
        // Prevent default vertical scroll
        event.preventDefault();
        // Convert vertical scroll to horizontal scroll
        container.scrollLeft += event.deltaY;
      }
    };

    const aboveElement = tabsNavAboveRef.current;
    const insideElement = tabsNavInsideRef.current;

    if (aboveElement) {
      aboveElement.addEventListener('wheel', handleWheel, { passive: false });
    }
    if (insideElement) {
      insideElement.addEventListener('wheel', handleWheel, { passive: false });
    }

    return () => {
      if (aboveElement) {
        aboveElement.removeEventListener('wheel', handleWheel);
      }
      if (insideElement) {
        insideElement.removeEventListener('wheel', handleWheel);
      }
    };
  }, [tabPlacement]);

  const activeTab = tabs.find(tab => tab.id === currentActiveTabId) || tabs[0];

  if (!tabs || tabs.length === 0) {
    return (
      <div className="tabs-section empty-tabs">
        <div className="empty-state">
          <p className="empty-tab-section-text">Empty tab section</p>
        </div>
      </div>
    );
  }

  const {
    tabButtonSize = 'hug',
    tabAlignment = 'left',
    isInnerPaddingEnabled,
    innerPaddingTop,
    innerPaddingRight,
    innerPaddingBottom,
    innerPaddingLeft
  } = layoutSettings;
  
  // Generate CSS classes based on layout settings
  const tabsNavigationClass = `tabs-navigation tabs-placement-${tabPlacement} tabs-alignment-${tabAlignment}`;
  const tabButtonClass = (tab, isActive) => {
    const baseClass = `tab-button tab-size-${tabButtonSize}`;
    const activeClass = isActive ? 'active' : '';
    return `${baseClass} ${activeClass}`.trim();
  };

  const innerBackgroundStyle = innerBackgroundData ? getBackgroundStyle(innerBackgroundData) : {};
  const borderStyle = borderData ? getBorderStyle(borderData, true) : {};
  
  const innerPaddingStyle = isInnerPaddingEnabled ? {
    paddingTop: `${innerPaddingTop || 0}px`,
    paddingRight: `${innerPaddingRight || 0}px`,
    paddingBottom: `${innerPaddingBottom || 0}px`,
    paddingLeft: `${innerPaddingLeft || 0}px`
  } : {};

  return (
    <div className="tabs-section">
      {tabPlacement === 'above' && (
        <div className={tabsNavigationClass} ref={tabsNavAboveRef}>
          {tabs.map((tab) => (
            <button
              key={tab.id}
              className={tabButtonClass(tab, currentActiveTabId === tab.id)}
              onClick={(e) => handleTabClick(tab.id, e)}
            >
              {tab.title || '( Untitled )'}
            </button>
          ))}
        </div>
      )}

      <div className={`tab-content-wrapper ${tabPlacement === 'inside' ? 'tabs-inside' : 'tabs-above'}`} style={{ ...borderStyle, ...innerPaddingStyle, ...innerBackgroundStyle }}>
        {tabPlacement === 'inside' && (
          <div className={tabsNavigationClass} ref={tabsNavInsideRef}>
            {tabs.map((tab) => (
              <button
                key={tab.id}
                className={tabButtonClass(tab, currentActiveTabId === tab.id)}
                onClick={(e) => handleTabClick(tab.id, e)}
              >
                {tab.title || '( Untitled )'}
              </button>
            ))}
          </div>
        )}

        <div className="tab-content">
          {activeTab ? (
            <div
              className="tab-content-area"
              data-velt-anchor={`tabs:${section.id}:${activeTab.id}`}
            >
              {activeTab.content && (activeTab.content.section || activeTab.content.sectionId) ? (
                <SectionsRoot
                  section={activeTab.content.section}
                  subpage={props.subpage}
                  isMobile={props.isMobile}
                  {...props}
                />
              ) : ''
              }
            </div>
          ) : (
            <div className="empty-tab">
              <p>No tabs available</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

TabsSection.propTypes = {
  section: PropTypes.shape({
    id: PropTypes.string.isRequired,
    type: PropTypes.string.isRequired,
    tabs: PropTypes.arrayOf(PropTypes.shape({
      id: PropTypes.string.isRequired,
      title: PropTypes.string.isRequired,
      content: PropTypes.shape({
        sectionId: PropTypes.string,
        section: PropTypes.object
      })
    }))
  }).isRequired,
  subpage: PropTypes.object,
  innerBackgroundData: PropTypes.object,
  borderData: PropTypes.object,
  activeTabId: PropTypes.string,
  setActiveTabId: PropTypes.func.isRequired
};

const mapStateToProps = (state, ownProps) => ({
  activeTabId: getActiveTabId(state, ownProps.section.id)
});

const mapDispatchToProps = (dispatch) => ({
  setActiveTabId: (sectionId, tabId) => dispatch(setActiveTabId(sectionId, tabId))
});

export default connect(mapStateToProps, mapDispatchToProps)(TabsSection);
